import { VideoConnection } from "../utils/video/videoConnection.ts";

export enum UserRoles {
    admin = 'admin',
    PA = 'Process Analyst',
    tech = 'tech',
    related = 'related',
    viewer = 'viewer',
    minor = 'minor',
    guest = 'guest',
};
// ROLES TODO: Implement a mapping system to associate user roles with their corresponding functional roles.

export type AuthStore = {
    authToken: string;
    user: null | AuthUser;
    error: null | string;
};

export type AuthUser = {
    timestamps: {
        created_at: string;
        updated_at: string;
        convert_date: string;
    };
    _id: string;
    firstName: string;
    lastName: string;
    active: boolean;
    role: UserRoles;
    shift: string;
    show_store_alert: boolean;
    hasUserChangedPassword: boolean;
    show_banner: boolean;
};

// export enum SystemTypeEnum {
//     ON_PREM = 'on-prem',
//     REMOTE = 'remote',
// }

//export type SystemTypeUnion = `${SystemTypeEnum}`;

export interface CameraDevice {
    _id: string;
    name: string;
    customId: string;
    date: string;
    status: string;
    serverIp: string;
    recorderPlayback: RecorderPlaybackType;
    recordingPeriodStart?: string;
    recordingPeriodEnd?: string;
    motionDetectionThreshold?: number;
    siteId: string;
    siteName: string;
    fisheye?: boolean;
}

export interface DeviceMiniInfo {
    _id: string;
    name: string;
    siteId: string;
    siteName: string;
    fisheye?: boolean;
}

export type DeviceIDsStore = DeviceMiniInfo[];

export interface POSDevice {
    _id: string;
    name: string;
    date: string;
    status: string;
    serverIp?: string;
    serverPort?: string;
    comPort?: string | null;
    siteId: string;
    siteName: string;
    cameraId: string;
    customId: string;
    connectionType: ConnectionTypes;
}

export enum DevicesListSortTypes {
    name = 'name',
    customId = 'customId',
    createdAt = 'createdAt',
    siteName = 'siteName',
    status = 'status',
}

export enum TransactionsListSortTypes {
    purchasedItem = 'purchasedItem',
    subtotal = 'subtotal',
    tax = 'tax',
    total = 'total',
    paymentMethod = 'paymentMethod',
    // posName = 'posName',
    siteName = 'siteName',
    createdAt = 'startedAt',
}

export enum SortOrder {
    ASC = 'ASC',
    DESC = 'DESC',
}

export interface DeviceListSort {
    type: DevicesListSortTypes;
    direction: SortOrder;
}

export interface DeviceListStore<T> extends DeviceListSort {
    devices: T[];
    pagesCount: number;
    page: number;
}

export interface GetDevicesArgs {
    siteId?: string;
    page?: number;
    searchValue?: string;
    sortBy?: DevicesListSortTypes;
    order?: SortOrder;
}

export type RecorderPlaybackType = 'motion' | 'continuous';


export interface AddOrEditCameraPayload extends Omit<CameraDevice, 'status' | 'date' | '_id'>
{
    page: number;
    searchValue?: string;
    searchedSiteId?: string;
}
export interface AddOrEditPOSPayload extends Omit<POSDevice, 'status' | 'date' | '_id' | 'customId'>
{
    page: number;
    searchValue?: string;
    searchedSiteId?: string;
}

export interface EditCameraParams {
    cameraNativeId: Required<CameraDevice>["_id"];
    params: AddOrEditCameraPayload;
}

export interface EditPOSParams {
    posNativeId: Required<POSDevice>["_id"]; // TODO: rename posNativeId => posId
    params: AddOrEditPOSPayload;
}

export interface ApiError {
    code: string;
    message: string;
    details: {
        key: string;
        label: string;
        value?: unknown;
    };
}

export type ApiResponse<T> = { success: T; errors: ApiError[] };

export enum ModalsViewKeysEnum {
    LOGOUT = 'logout',
}

//export type ModalsViewKeysUnion = `${ModalsViewKeysEnum}`;

export type ModalsViewStore = {
    [ModalsViewKeysEnum.LOGOUT]: boolean;
};

export interface SiteData {
    active: boolean;
    label: string;
    isCustom?: boolean;
    value: {
        owner_id: string;
        store_id: string;
    };
}

export interface SitesStore {
    sites: SiteData[];
    selectedSite: SiteData | null;
}

export interface TimelineItem {
    start: number; // utc timestamp
    end: number | 'live'; // utc timestamp
    recordingId: string;
}
export type Timeline = TimelineItem[];

export interface TimelineStore {
    timeline: Timeline;
}

export interface SitesCamerasStore {
    sitesCameras: Record<string, CameraDevice[]>;
}

export interface Camera {
    _id: string;
    name: string;
    customId: string;
    date: string;
    order?: number;
    siteId: string;
    fisheye?: boolean;
}

export interface VideoWallStore {
    cameras: Camera[];
}

export interface CameraUpdateData {
    cameraId: string;
    order: number;
}

export enum VideoWallModes {
    Default = 'Default',
    Edit = 'Edit',
    FullScreen = 'FullScreen'
}

export enum VideoWallDimensions {
    three = 3,
    four = 4,
    five = 5,
    six = 6,
}


export interface LeftMenuViewStore {
    isOpen: boolean;
    openNestedMenuTitle: null | string;
}

export interface VideoWallViewStore {
    mode: VideoWallModes;
    isAddCameraMenuOpen: boolean;
    dimension: VideoWallDimensions;
    manualDimension: boolean;
}

export interface VideoConnectionsStore {
    connections: Record<string, VideoConnection>;
}

interface BasicSnapshotArgs {
    cameraId: string;
    timestamp: number;
    name?: string;
    note?: string;
    posOverlay: boolean;
}

export interface HandlePostSnapshotArgs extends BasicSnapshotArgs {
    blob: Blob;
}

export interface HandleCreateSnapshotArgs extends BasicSnapshotArgs {
    videoElement?: HTMLVideoElement;
    ptzCanvas?: HTMLCanvasElement | null;
}
export enum ArchiveItemTypes {
    snapshot = 'snapshot',
    clip = 'clip',
}

export interface ArchiveItem {
    _id: string;
    cameraId: string;
    cameraName: string;
    siteId: string;
    siteName: string;
    name: string;
    date: string;
    time?: string;
    kind: ArchiveItemTypes;
    startTime?: string;
    endTime?: string;
    note?: string;
    posOverlay: boolean;
    posTransactions?: POSTransaction[];
    ptzParams?: PtzParameters;
}

export enum ArchiveItemAccessTypes {
    Everyone = 'everyone',
    Private = 'private',
}

export interface ArchiveStore {
    items: ArchiveItem[];
    pagesCount: number;
    page: number;
}

export interface ArchiveFilters {
    siteId?: string;
    POSId?: string;
    sortOrder?: SortOrder;
    dateRangeFrom?: Date | null;
    dateRangeTo?: Date | null;
    timeRangeFrom?: string;
    timeRangeTo?: string;
    type?: ArchiveItemTypes;
    accessType?: ArchiveItemAccessTypes;
    searchValue?: string;
    page?: number;
}

export interface EditArchiveFormData {
    name?: string;
    note?: string;
    startTime?: string;
    endTime?: string;
    date?: Date;
    accessType?: ArchiveItemAccessTypes;
    posOverlay?: boolean;

}

export interface HandledArchiveFormData {
    name?: string;
    note?: string;
    startTime?: number;
    endTime?: number;
    accessType?: ArchiveItemAccessTypes;
    posOverlay: boolean;
}

export interface HandleEditArchiveArgs {
    archiveItemId: string;
    kind: string;
    editParams: EditArchiveFormData;
}

export interface SearchFilters {
    page: number;
    searchValue?: string;
    searchedSiteId?: string;
}

export interface DeleteDevicesArgs extends SearchFilters {
    devicesIds: CameraDevice['_id'][] | POSDevice['_id'][];
}

export interface DeleteCameraDevicesResponse {
    cameras: CameraDevice[];
    pagesCount: number;
    page: number;
}

export interface DeletePOSDevicesResponse {
    pos: POSDevice[];
    pagesCount: number;
    page: number;
}

export interface DeleteArchiveItemsArgs {
    ids: string[];
    page: number;
}

export interface POSTransactionLine {
    posId: string;
    name: string;
    amount: number;
    cost: number;
}
export enum POSPaymentMethod {
    cash = 'cash', 
    visa = 'visa',
    mastercard = 'mastercard',
    unknown = 'unknown',
};

export interface POSTransaction {
    _id: string;
    posId: string;
    posName: string;
    posCustomId: string;
    siteId: string;
    siteName: string;
    cameraId: string;
    goods: POSTransactionLine[];
    subtotal: number;
    total: number;
    tax: number;
    paymentMethod: POSPaymentMethod;
    date: Date;
    thumbnail: string;
    startedAt: Date;
    posNativeId?: string;
    content: string[];
}
export interface GetTransactionsArgs {
    siteId?: string;
    posId?: string;
    cameraId?: string;
    paymentMethod?: POSPaymentMethod | null;
    startDate?: Date | null;
    endDate?: Date | null;
    startTime?: string | null;
    endTime?: string | null;
    totalFrom?: number | null;
    totalTo?: number | null;
    page: number | undefined;
    searchValue?: string;
    sortBy?: TransactionsListSortTypes;
    order?: SortOrder;
}

export interface GetPlaybackTransactionsArgs {
    cameraId: string;
    startTime: number;
    endTime?: number;
    limit?: number;
    direction?: NewTransactionDirection;
}

export interface GetTimelineTransactionsArgs extends GetPlaybackTransactionsArgs {
    isClip?: boolean;
}

export interface TransactionListSort {
    type: TransactionsListSortTypes;
    direction: SortOrder;
}
export interface TransactionListStore extends TransactionListSort {
    transactions: POSTransaction[];
    pagesCount: number;
    page: number;
}

export interface IFrameLineItem {
    url: string;
    frameStartTime: number;
    frameEndTime: number;
}

export interface PtzParameters {
    pan: number;
    tilt: number;
    fov: number;
}

export interface CreateClipArgs {
    startTime?: number;
    endTime?: number;
    name?: string;
    note?: string;
    posOverlay: boolean;
    ptzParams?: PtzParameters;
}

export interface ISelectedClipToSave {
    recordingId?: string;
    endRecordingId?: string;
    startTime?: number;
    endTime?: number;
}

export interface ArchiveItemToSave {
    kind: ArchiveItemTypes;
    siteName: string;
    cameraId: string;
    cameraName: string;
    recordingId?: string;
    timestamp?: number;
    startTime?: number;
    endTime?: number;
    videoType?: VideoTypes;
}

export enum VideoTypes {
    live = 'live',
    playback = 'playback',
}

export interface UserSettingsStore {
    liveModeOn: boolean | null;
    camerasWithPOSOff: string[];
}

export interface GetPlaybackTransactionsResponse {
    cameraId: string;
    transactions: POSTransaction[];
    direction?: NewTransactionDirection;
}

export enum ConnectionTypes {
    wireless = 'wireless',
    wired = 'wired',
}

export enum NewTransactionDirection {
    prev = 'prev',
    next = 'next',
}

export interface GetNewTransactionTimeArgs {
    cameraId: string,
    currentTimestamp: number,
    direction: NewTransactionDirection,
}
export interface GetNewTransactionTimeResponse {
    direction: NewTransactionDirection,
    newTransaction: { id: string, startTimestamp: number } | null,
}

export interface ComPortsStore {
    ports: string[];
}


export enum DeviceStatuses {
    online = 'online',
    offline = 'offline',
}

export interface ITransactionsListFilters {
    selectedSite: SiteData | null,
    selectedPOS: DeviceMiniInfo | null,
    startDate: Date | null;
    endDate: Date | null;
    startTime: string | undefined;
    endTime: string | undefined;
}

export interface PosTooltip {
    name: string;
    clientX: number;
    short: boolean;
}

export interface PosDeviceStatus {
    posId: string;
    status: DeviceStatuses;
    cameraId: string;
}

export enum TransactionDisplayType {
    TABLE = 'table',
    CARDS = 'cards',
}
